import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

import LogoHeader from "../components/LogoHeader";
import api from "../api/axiosConfig";
import { useNavigate } from "react-router-dom";




export default function NotificationsPage() {
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const [invites, setInvites] = useState([]);
  const [shoutouts, setShoutouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blockMsg, setBlockMsg] = useState("");


  const API_BASE =
    process.env.REACT_APP_API_BASE || "http://localhost:5000";

  /* -----------------------------
     Load notifications
  ------------------------------ */
  useEffect(() => {
    if (!user) return;

    const loadNotifications = async () => {
      try {
        const res = await api.get(`/notifications/${user.id}`);
        setInvites(res.data.notifications || []);

        // Optional: shoutouts later
        setShoutouts([]); // placeholder
      } catch (err) {
        console.error("Failed to load notifications", err);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [user?.id]);

  /* -----------------------------
     Invite response
  ------------------------------ */
const respondToInvite = async (inviteId, action) => {
  try {
    const res = await api.post(`/invites/${inviteId}/respond`, { action });

    setInvites((prev) => prev.filter((i) => i.id !== inviteId));

   if (action === "accepted" && res.data?.session) {
  // 🔑 Notify the app that a moment is now active
  window.dispatchEvent(
    new CustomEvent("moment-accepted", {
      detail: {
        session: res.data.session,
      },
    })
  );

  // Optional: still update URL for deep-linking
  sessionStorage.removeItem("moment_suppressed");

}

  } catch (err) {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  const activeMomentId = err?.response?.data?.momentId;

  if (status === 409 && code === "ALREADY_IN_MOMENT") {
    setBlockMsg("You’re already in a moment. End it before accepting a new invitation.");

    
    return;
  }

  console.error("Failed to respond to invite", err);
}

};


  /* -----------------------------
     Render helpers
  ------------------------------ */
  const renderInvite = (invite) => (
    <Box
      key={invite.id}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        py: 1.5,
      }}
    >
      <Avatar
        src={
          invite.profilePic
            ? invite.profilePic.startsWith("http")
              ? invite.profilePic
              : `${API_BASE}${invite.profilePic}`
            : undefined
        }
      />

      <Box sx={{ flex: 1 }}>
        <Typography fontWeight={600}>
          {invite.username}
        </Typography>

        <Typography fontSize="0.85rem" color="text.secondary">
          {invite.note || "Invited you to connect"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() =>
            respondToInvite(invite.id, "accepted")
          }
        >
          Accept
        </Button>

        <Button
          size="small"
          color="inherit"
          onClick={() =>
            respondToInvite(invite.id, "declined")
          }
        >
          Not Now
        </Button>
      </Box>
    </Box>
  );

  /* -----------------------------
     Main render
  ------------------------------ */
  return (
    <Box sx={{ px: 2, pt: 1.5 }}>
      <LogoHeader />

      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "1.1rem",
          mt: 2,
          mb: 1,
        }}
      >
        Notifications
      </Typography>

      {loading && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && invites.length === 0 && shoutouts.length === 0 && (
        <Typography
          sx={{
            mt: 4,
            textAlign: "center",
            color: "text.secondary",
            fontSize: "0.9rem",
          }}
        >
          No new activity right now
        </Typography>
      )}

      {/* INVITES */}
      {invites.length > 0 && (
        <>
          <Typography
            sx={{
              mt: 3,
              mb: 1,
              fontWeight: 600,
              fontSize: "0.9rem",
              color: "text.secondary",
            }}
          >
            Invitations
          </Typography>

          {invites.map(renderInvite)}
        </>
      )}

      {/* SHOUTOUTS */}
      {shoutouts.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />

          <Typography
            sx={{
              mb: 1,
              fontWeight: 600,
              fontSize: "0.9rem",
              color: "text.secondary",
            }}
          >
            Shoutouts
          </Typography>

          {/* Placeholder — we’ll wire this later */}
        </>
      )}

      <Snackbar
  open={!!blockMsg}
  autoHideDuration={4000}
  onClose={() => setBlockMsg("")}
  anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
>
  <Alert
    severity="warning"
    onClose={() => setBlockMsg("")}
    sx={{ borderRadius: 2, fontWeight: 600 }}
  >
    {blockMsg}
  </Alert>
</Snackbar>



    </Box>
  );
}
